import { Container } from "@mantine/core";
import { FunctionComponent, useEffect } from "react";
import {
  Control,
  DefaultValues,
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
  UseFormSetValue,
  useWatch,
  WatchObserver,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormFieldInput } from "#shared/view/components/Form/FormFieldInput";
import { Button } from "#shared/view/designSystem/Button";

import { Title } from "../Title";
import { FieldType, FormWatchValues } from "./types";

type FormProps<T extends FieldValues> = {
  sections: {
    title: string;
    fields: FieldType<T>[];
    isVisible?: boolean | ((state: FormWatchValues<T>) => boolean);
    FooterComponent?: FunctionComponent<{
      fields: FormWatchValues<T>;
      control: Control<T>;
    }>;
  }[];
  onSubmit: SubmitHandler<T>;
  FooterComponent?: FunctionComponent<{
    control: Control<T>;
  }>;
  SubmitComponent?: FunctionComponent<{
    isLoading?: boolean;
    fields?: FormWatchValues<T>;
    disabled?: (fields: FormWatchValues<T>) => boolean;
  }>;
  isLoading?: boolean;
  disabled?: (values: FormWatchValues<T>) => boolean;
  defaultValues?: DefaultValues<T>;
  BlockerComponent?: FunctionComponent;
  watchObserver?: (setValue: UseFormSetValue<T>) => WatchObserver<T>;
  readOnly?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
};

const DefaultSubmitComponent = <T extends FieldValues>({
  isLoading,
  fields,
  disabled,
}: {
  isLoading?: boolean;
  fields?: FormWatchValues<T>;
  disabled?: (fields: FormWatchValues<T>) => boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Button
      type="submit"
      disabled={disabled && fields !== undefined && fields !== null ? disabled(fields) : undefined}
      loading={isLoading}
      mt="xs"
    >
      {t("common.validate")}
    </Button>
  );
};

export const Form = <T extends FieldValues>({
  sections,
  onSubmit,
  FooterComponent = () => <></>,
  isLoading,
  disabled,
  defaultValues,
  SubmitComponent = DefaultSubmitComponent,
  BlockerComponent = () => <></>,
  watchObserver,
  readOnly = false,
  size = "sm",
}: FormProps<T>) => {
  const formMethods = useForm<T>({ defaultValues });
  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    watch,
    reset,
    setValue,
  } = formMethods;

  const allFields = useWatch({ control });

  useEffect(() => {
    if (watchObserver) {
      const subscription = watch(watchObserver(setValue));

      return () => subscription.unsubscribe();
    }
  }, [watch, watchObserver, setValue]);

  useEffect(() => {
    if (!isDirty) {
      reset(defaultValues);
    }
  }, [reset, defaultValues, isDirty]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container size={size}>
          {sections.map(({ isVisible: isSectionVisible = true, ...section }) => {
            if (typeof isSectionVisible === "function") {
              if (!isSectionVisible(allFields)) {
                return null;
              }
            } else {
              if (!isSectionVisible) {
                return null;
              }
            }

            return (
              <section
                key={section.title}
                style={{
                  marginBottom: 18,
                }}
              >
                <Title order={3}>{section.title}</Title>
                {section.fields.map(({ isVisible: isFieldVisible = true, ...field }) => {
                  if (typeof isFieldVisible === "function") {
                    if (!isFieldVisible(allFields)) {
                      return null;
                    }
                  } else {
                    if (!isFieldVisible) {
                      return null;
                    }
                  }

                  return (
                    <FormFieldInput
                      key={field.key}
                      field={field}
                      defaultValues={defaultValues}
                      readOnly={readOnly}
                      errors={errors}
                    />
                  );
                })}
                {section.FooterComponent && (
                  <section.FooterComponent fields={allFields} control={control} />
                )}
              </section>
            );
          })}
          <BlockerComponent />
          <FooterComponent control={control} />
          <SubmitComponent disabled={disabled} fields={allFields} isLoading={isLoading} />
        </Container>
      </form>
    </FormProvider>
  );
};
